import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { enviarCodigoValidacao, validarCodigo } from '../services';
import logo from '../assets/concreto_tec_logo.png';

const TAMANHO_CODIGO = 6;
const ESPERA_REENVIO = 60; // segundos entre reenvios
const ANGULOS_LEQUE = [-20, -12, -4, 4, 12, 20]; // inclinação de cada "carta" no leque
const FAISCAS = Array.from({ length: 12 }, (_, i) => (i * 30 * Math.PI) / 180);

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const reduzMovimento = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

// Tela de verificação de e-mail: recebe o código de 6 dígitos e ativa o usuário.
// Animação: borda percorrida a cada dígito; no envio as caixas viram um leque de cartas
// e se fundem num selo, que fica neutro enquanto espera e depois verde (ok) ou vermelho (erro).
function VerificarEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const emailInicial = location.state?.email || searchParams.get('email') || '';
  const codigoReenviado = Boolean(location.state?.codigoReenviado);
  const semAnimacao = useMemo(reduzMovimento, []);

  const [email, setEmail] = useState(emailInicial);
  const [digitos, setDigitos] = useState(Array(TAMANHO_CODIGO).fill(''));
  const [fase, setFase] = useState('idle'); // idle | leque | verificando | sucesso | erro
  const [reenviando, setReenviando] = useState(false);
  const [espera, setEspera] = useState(codigoReenviado ? ESPERA_REENVIO : 0);
  const [erro, setErro] = useState('');
  const [info, setInfo] = useState(codigoReenviado ? 'Enviamos um código para o seu e-mail.' : '');

  const inputsRef = useRef([]);
  const cartasRef = useRef([]);
  const bordasRef = useRef([]);
  const palcoRef = useRef(null);
  const lequeRef = useRef({ transformacoes: [], animacoes: [] });
  const verificarRef = useRef(null);

  const codigo = digitos.join('');
  const codigoCompleto = codigo.length === TAMANHO_CODIGO;
  const ocupado = fase !== 'idle';
  const mostrarSelo = ['verificando', 'sucesso', 'erro'].includes(fase);

  // Contagem regressiva do botão de reenvio.
  useEffect(() => {
    if (espera <= 0) return undefined;
    const timer = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [espera]);

  useEffect(() => {
    if (emailInicial) inputsRef.current[0]?.focus();
  }, [emailInicial]);

  // Envia sozinho ao completar o código (só quando o e-mail já veio do login).
  useEffect(() => {
    if (!emailInicial || !codigoCompleto || fase !== 'idle') return undefined;
    const timer = setTimeout(() => verificarRef.current?.(), 300);
    return () => clearTimeout(timer);
  }, [emailInicial, codigo, codigoCompleto, fase]);

  const focar = (index) => inputsRef.current[Math.max(0, Math.min(index, TAMANHO_CODIGO - 1))]?.focus();

  // Traço que percorre a borda da caixa a cada dígito digitado.
  const correrBorda = (index) => {
    const borda = bordasRef.current[index];
    if (!borda || semAnimacao) return;
    borda.animate(
      [
        { strokeDashoffset: 1, opacity: 1 },
        { strokeDashoffset: 0, opacity: 1, offset: 0.75 },
        { strokeDashoffset: 0, opacity: 0 },
      ],
      { duration: 480, easing: 'ease-out' }
    );
  };

  const preencherAPartirDe = (index, texto) => {
    const numeros = texto.replace(/\D/g, '').slice(0, TAMANHO_CODIGO - index).split('');
    if (!numeros.length) return;
    setDigitos((atual) => {
      const novo = [...atual];
      numeros.forEach((n, i) => { novo[index + i] = n; });
      return novo;
    });
    numeros.forEach((_, i) => setTimeout(() => correrBorda(index + i), i * 45));
    focar(index + numeros.length);
  };

  // Junta as caixas no centro em leque (pivô abaixo da caixa) e depois as recolhe.
  const abrirLeque = async () => {
    const palco = palcoRef.current.getBoundingClientRect();
    const centro = palco.left + palco.width / 2;
    const cartas = cartasRef.current;
    const transformacoes = cartas.map((carta, i) => {
      const r = carta.getBoundingClientRect();
      return `translateX(${centro - (r.left + r.width / 2)}px) rotate(${ANGULOS_LEQUE[i]}deg)`;
    });

    const abrir = cartas.map((carta, i) => carta.animate(
      [{ transform: 'none' }, { transform: transformacoes[i] }],
      { duration: 560, delay: i * 45, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' }
    ));
    await Promise.all(abrir.map((a) => a.finished));

    const recolher = cartas.map((carta, i) => carta.animate(
      [{ transform: transformacoes[i], opacity: 1 }, { transform: `${transformacoes[i]} scale(.6)`, opacity: 0 }],
      { duration: 240, easing: 'ease-in', fill: 'forwards' }
    ));
    abrir.forEach((a) => a.cancel());
    await Promise.all(recolher.map((a) => a.finished));
    lequeRef.current = { transformacoes, animacoes: recolher };
  };

  // Caminho inverso do leque, usado quando o código é recusado.
  const devolverCartas = async () => {
    const { transformacoes, animacoes } = lequeRef.current;
    animacoes.forEach((a) => a.cancel());
    const voltar = cartasRef.current.map((carta, i) => carta.animate(
      [
        { transform: `${transformacoes[i]} scale(.6)`, opacity: 0 },
        { transform: transformacoes[i], opacity: 1, offset: 0.35 },
        { transform: 'none', opacity: 1 },
      ],
      { duration: 520, delay: (TAMANHO_CODIGO - 1 - i) * 40, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' }
    ));
    await Promise.all(voltar.map((a) => a.finished));
  };

  const verificar = async () => {
    if (ocupado || !email || !codigoCompleto) return;
    setErro('');
    setInfo('');
    setFase('leque');

    // Requisição corre em paralelo com a animação; a falha volta como valor.
    const requisicao = validarCodigo(email.trim(), codigo).then(() => null, (error) => error);
    if (!semAnimacao) await abrirLeque();
    setFase('verificando');
    const [falha] = await Promise.all([requisicao, esperar(semAnimacao ? 0 : 700)]);

    if (!falha) {
      setFase('sucesso');
      await esperar(semAnimacao ? 600 : 1300);
      navigate('/', { replace: true, state: { email: email.trim(), mensagem: 'E-mail validado com sucesso. Faça login para continuar.' } });
      return;
    }

    setFase('erro');
    await esperar(semAnimacao ? 400 : 900);
    setFase('idle');
    setDigitos(Array(TAMANHO_CODIGO).fill(''));
    setErro(falha.message || 'Código inválido ou expirado.');
    if (!semAnimacao) await devolverCartas();
    focar(0);
  };
  verificarRef.current = verificar;

  const handleChange = (index, value) => {
    setErro('');
    if (!value) {
      setDigitos((atual) => atual.map((d, i) => (i === index ? '' : d)));
      return;
    }
    preencherAPartirDe(index, value);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digitos[index] && index > 0) {
      event.preventDefault();
      setDigitos((atual) => atual.map((d, i) => (i === index - 1 ? '' : d)));
      focar(index - 1);
    } else if (event.key === 'ArrowLeft') {
      focar(index - 1);
    } else if (event.key === 'ArrowRight') {
      focar(index + 1);
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    setErro('');
    preencherAPartirDe(index, event.clipboardData.getData('text'));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    verificar();
  };

  const handleReenviar = async () => {
    if (!email || espera > 0) return;
    setErro('');
    setInfo('');
    setReenviando(true);
    try {
      await enviarCodigoValidacao(email.trim());
      setInfo('Se o e-mail estiver cadastrado e pendente de validação, um novo código foi enviado.');
      setEspera(ESPERA_REENVIO);
    } catch (error) {
      setErro(error.message || 'Não foi possível reenviar o código.');
    } finally {
      setReenviando(false);
    }
  };

  const textoBotao = {
    leque: 'Verificando...',
    verificando: 'Verificando...',
    sucesso: 'E-mail verificado',
    erro: 'Código recusado',
  }[fase] || 'Verificar e-mail';

  return (
    <div className="login-page login-shell min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 py-10 sm:py-0">
      <div className="login-card w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-sm sm:shadow-lg">
        <div className="login-header px-5 pt-6 text-center sm:px-6">
          <div className="login-mark mx-auto flex items-center justify-center">
            <img src={logo} alt="Logo Kretos Lab" className="h-14 w-14 object-contain" />
          </div>
          <h1 className="login-title mt-4 text-2xl font-semibold text-slate-900">Verifique seu e-mail</h1>
          <p className="login-subtitle mt-1 text-sm text-slate-500">
            {emailInicial ? (
              <>Enviamos um código de {TAMANHO_CODIGO} dígitos para <span className="font-medium text-slate-700">{emailInicial}</span>.</>
            ) : (
              <>Informe seu e-mail e o código de {TAMANHO_CODIGO} dígitos recebido.</>
            )}
          </p>
        </div>
        <div className="login-body px-5 pb-6 pt-5 sm:px-6">
          <form onSubmit={handleSubmit} className="login-form space-y-4">
            {!emailInicial && (
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  readOnly={ocupado}
                  required
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="codigo-0" className="block text-center text-sm font-medium text-slate-700">
                Código de verificação
              </label>
              <div
                ref={palcoRef}
                className={`otp-stage relative flex justify-center gap-2 py-1 ${ocupado ? 'pointer-events-none' : ''}`}
              >
                {digitos.map((digito, index) => (
                  <div
                    key={index}
                    ref={(el) => { cartasRef.current[index] = el; }}
                    className={`otp-slot relative ${semAnimacao && ocupado ? 'invisible' : ''}`}
                  >
                    <input
                      id={`codigo-${index}`}
                      ref={(el) => { inputsRef.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      maxLength={TAMANHO_CODIGO}
                      value={digito}
                      readOnly={ocupado}
                      onChange={(event) => handleChange(index, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                      onPaste={(event) => handlePaste(index, event)}
                      onFocus={(event) => event.target.select()}
                      aria-label={`Dígito ${index + 1}`}
                      className={`h-12 w-10 rounded-md border bg-slate-50 text-center text-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 sm:w-12 ${
                        erro ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                      }`}
                    />
                    <svg className="otp-slot__spark" aria-hidden="true">
                      <rect
                        ref={(el) => { bordasRef.current[index] = el; }}
                        width="100%"
                        height="100%"
                        rx="6"
                        pathLength="1"
                      />
                    </svg>
                  </div>
                ))}

                {mostrarSelo && (
                  <div className={`otp-seal otp-seal--${fase}`} aria-hidden="true">
                    <svg viewBox="0 0 96 96">
                      <g className="otp-seal__sparks">
                        {FAISCAS.map((angulo) => (
                          <line
                            key={angulo}
                            x1={48 + Math.cos(angulo) * 37}
                            y1={48 + Math.sin(angulo) * 37}
                            x2={48 + Math.cos(angulo) * 45}
                            y2={48 + Math.sin(angulo) * 45}
                          />
                        ))}
                      </g>
                      <rect className="otp-seal__track" x="26" y="26" width="44" height="44" rx="13" />
                      <rect className="otp-seal__seg" x="26" y="26" width="44" height="44" rx="13" pathLength="1" />
                      <rect className="otp-seal__ring" x="26" y="26" width="44" height="44" rx="13" pathLength="1" />
                      <path className="otp-seal__check" d="M38 49 l7 7 l13 -15" pathLength="1" />
                      <path className="otp-seal__x" d="M41 41 l14 14 M55 41 l-14 14" pathLength="1" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-center text-xs text-slate-400" aria-live="polite">
                {fase === 'sucesso' ? 'E-mail verificado! Redirecionando...' : 'O código expira em 15 minutos.'}
              </p>
            </div>

            {erro ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-center text-xs text-red-700">{erro}</p>
            ) : null}
            {info ? (
              <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-center text-xs text-blue-700">{info}</p>
            ) : null}

            <button
              type="submit"
              className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed ${
                fase === 'sucesso' ? 'bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-70'
              }`}
              disabled={ocupado || !codigoCompleto || !email}
            >
              {textoBotao}
            </button>
          </form>
          <div className="login-actions mt-4 flex items-center justify-between text-xs text-slate-500">
            <Link to="/" className="text-blue-600 hover:text-blue-700">
              Voltar para o login
            </Link>
            <button
              type="button"
              onClick={handleReenviar}
              disabled={!email || reenviando || espera > 0 || ocupado}
              className="text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {reenviando ? 'Reenviando...' : espera > 0 ? `Reenviar código (${espera}s)` : 'Reenviar código'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerificarEmailPage;
